import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/auth';

export function ProfilePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const generateAnalysis = useMutation({
    mutationFn: () => api.readings.generateDreamerAnalysis(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dreamer-analysis'] });
    },
  });

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.users.getProfile(),
    enabled: !!user,
  });

  const { data: stats } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => api.users.getStats(),
    enabled: !!user,
  });

  const { data: analysis } = useQuery({
    queryKey: ['dreamer-analysis'],
    queryFn: () => api.readings.getDreamerAnalysis(),
    enabled: !!user,
  });

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <Link to="/login" className="btn-primary">Login</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-display font-bold text-white mb-8">Profile</h1>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Dreams" value={stats.totalDreams} />
          <StatCard label="Public" value={stats.publicDreams} />
          <StatCard label="Private" value={stats.privateDreams} />
          <StatCard label="Avg Stress" value={stats.averageStressScore.toFixed(1)} />
        </div>
      )}

      {/* Profile Info */}
      <div className="card mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">Account</h2>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-dream-400">Email</span>
            <span className="text-dream-200">{profile?.email}</span>
          </div>
          {profile?.name && (
            <div className="flex justify-between">
              <span className="text-dream-400">Name</span>
              <span className="text-dream-200">{profile.name}</span>
            </div>
          )}
          {profile?.city && (
            <div className="flex justify-between">
              <span className="text-dream-400">Location</span>
              <span className="text-dream-200">
                {[profile.city, profile.state, profile.country].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-dream-400">Visibility</span>
            <span className="text-dream-200">{profile?.visibility}</span>
          </div>
          {profile?.subscription && (
            <div className="flex justify-between">
              <span className="text-dream-400">Subscription</span>
              <span className="text-dream-200 capitalize">{profile.subscription.status}</span>
            </div>
          )}
        </div>
      </div>

      {/* Dreamer Analysis */}
      {analysis && (
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Your Dreamer Analysis</h2>
          <div className="space-y-4">
            <AnalysisSection title="Embodied Archetype" content={analysis.embodiedArchetype} />
            <AnalysisSection title="Dream Patterns" content={analysis.dreamPatternAssessment} />
            {analysis.unseenShadow && (
              <AnalysisSection title="Unseen Shadow" content={analysis.unseenShadow} />
            )}
            {analysis.aspirationalArchetype && (
              <AnalysisSection title="Aspirational Archetype" content={analysis.aspirationalArchetype} />
            )}
            {analysis.nextEvolutionarySteps && (
              <AnalysisSection title="Next Steps" content={analysis.nextEvolutionarySteps} />
            )}
          </div>
        </div>
      )}

      {!analysis && stats && stats.totalDreams >= 5 && (
        <div className="card text-center">
          <h2 className="text-lg font-semibold text-white mb-2">Get Your Dreamer Analysis</h2>
          <p className="text-dream-300 mb-4">
            You have enough dreams recorded. Generate a comprehensive analysis of your dream patterns.
          </p>
          {generateAnalysis.isError && (
            <p className="text-red-400 text-sm mb-3">{generateAnalysis.error.message}</p>
          )}
          {generateAnalysis.isSuccess ? (
            <p className="text-dream-300 text-sm">Analysis queued — check back shortly.</p>
          ) : (
            <button
              className="btn-primary"
              disabled={generateAnalysis.isPending}
              onClick={() => generateAnalysis.mutate()}
            >
              {generateAnalysis.isPending ? 'Generating...' : 'Generate Analysis'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="card text-center">
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-dream-400 text-sm">{label}</div>
    </div>
  );
}

function AnalysisSection({ title, content }: { title: string; content: string }) {
  return (
    <div>
      <h3 className="text-sm font-medium text-dream-400 mb-1">{title}</h3>
      <p className="text-dream-200">{content}</p>
    </div>
  );
}
